/**
 * Formatting. Every function here takes a value the SERVER sent and turns it into a string.
 *
 * THERE IS NO CLOCK IN THIS APP AND THERE MUST NOT BE. Two reasons, and the second is the one that
 * matters:
 *
 *  1. C11 (adr/0013) bans `Date.now()`, `new Date()` and `performance.now()` across `web/`,
 *     enforced by `no-restricted-syntax` in the ESLint config with exactly one exempt file in
 *     `src/`. Nothing under `web/authors` is exempt.
 *  2. Every figure the dashboard and the statistics reads return is bucketed on THE UTC CALENDAR
 *     DAY RESOLVED ON THE SERVER CLOCK. `getDashboard` says so about both `from` and `to`: "A bare
 *     `date` carries no zone, and every figure this route returns is bucketed on the UTC day (D9)
 *     — so without that sentence a client in UTC+13 and the numbers it renders disagree about
 *     which day it asked for." A browser that computed its own "today" or its own "last 30 days"
 *     would label the server's numbers with its own range. So ranges come from the RESPONSE
 *     (`Dashboard.from` / `Dashboard.to`) or from an explicit choice, never from the page.
 *
 * Consequently there is no `timeAgo` here. Absolute instants only.
 */
import { i18n } from '../../i18n';

/**
 * Money.
 *
 * ALL AMOUNTS ARE INTEGER MINOR UNITS WITH A CURRENCY — `MinorUnits` and `CurrencyCode` in the
 * generated types, never a float. The division by 100 happens once, here, at the edge of the
 * screen, and never in a sum.
 *
 * ISO 4217 has currencies with zero and three minor digits, so the exponent is taken from
 * `Intl.NumberFormat` rather than assumed to be two — for EUR it is 2 and for JPY it is 0, and
 * hard-coding 2 would print a hundred times the real figure in Tokyo.
 *
 * A NEGATIVE FIGURE IS RENDERED AS NEGATIVE. `availableMinor` "may be negative" (a refunded tip is
 * a real deduction) and is never clamped; `Math.abs` anywhere near this function is a bug.
 */
export function formatMoney(minor: number, currency: string, locale = CABINET_LOCALE): string {
  const formatter = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    currencyDisplay: 'narrowSymbol',
  });
  // `maximumFractionDigits` is optional on the resolved options type. It is always present for a
  // currency format in practice; two is the ISO 4217 default and the right thing to fall back on
  // rather than dividing by one and printing a hundred times the amount.
  const digits = formatter.resolvedOptions().maximumFractionDigits ?? 2;
  return formatter.format(minor / 10 ** digits);
}

/** A plain count, grouped. Never used for money. */
export function formatCount(value: number, locale = CABINET_LOCALE): string {
  return new Intl.NumberFormat(locale).format(value);
}

/** A ratio in 0..1 as a percentage. `readThrough.coverage` and friends. */
export function formatPercent(ratio: number, locale = CABINET_LOCALE): string {
  return new Intl.NumberFormat(locale, {
    style: 'percent',
    maximumFractionDigits: 1,
  }).format(ratio);
}

/**
 * A `ServerTime` instant. Parsing a value the server gave us is NOT a clock read — the ESLint rule
 * bans only the zero-argument form, and its own message says so: "Parsing an instant you were
 * given — new Date(value) — is allowed and is not a clock read."
 */
export function formatInstant(value: string, locale = CABINET_LOCALE): string {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }
  return new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' }).format(
    parsed,
  );
}

/** A `format: date` value — a UTC calendar day, printed as a day and not as a local midnight. */
export function formatDay(value: string, locale = CABINET_LOCALE): string {
  const parsed = new Date(`${value}T00:00:00Z`);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }
  return new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeZone: 'UTC' }).format(parsed);
}

/** A `YYYY-MM` period. Months are UTC everywhere money is measured. */
export function formatPeriod(period: string, locale = CABINET_LOCALE): string {
  const parsed = new Date(`${period}-01T00:00:00Z`);
  if (Number.isNaN(parsed.getTime())) {
    return period;
  }
  return new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: 'long',
    timeZone: 'UTC',
  }).format(parsed);
}

/** The range a response covers, both bounds inclusive UTC days. */
export function formatDayRange(from: string, to: string, locale = CABINET_LOCALE): string {
  return i18n.t('format.dayRange', {
    ns: 'shell',
    from: formatDay(from, locale),
    to: formatDay(to, locale),
  });
}

/** A `format: date` day as a chart's axis names it - "Sep 21" - a UTC day, never a local midnight. */
export function formatShortDay(value: string, locale = CABINET_LOCALE): string {
  const parsed = new Date(`${value}T00:00:00Z`);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }
  return new Intl.DateTimeFormat(locale, {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  }).format(parsed);
}

/**
 * A range as the period button shows it (design 24, T15) - "Sep 9 – Oct 8, 2026", "Aug 1 – 10,
 * 2026" - UTC days, both included, the year written once. ICU's thin spaces around the dash become
 * plain ones, so the words read and compare the same on every runtime.
 */
export function formatDaySpan(
  { from, to }: { readonly from: string; readonly to: string },
  locale = CABINET_LOCALE,
): string {
  const start = new Date(`${from}T00:00:00Z`);
  const end = new Date(`${to}T00:00:00Z`);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
    return formatDayRange(from, to, locale);
  }
  return new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeZone: 'UTC' })
    .formatRange(start, end)
    .replace(/\u2009/g, ' ');
}

/** English is American, as the cabinet's English words are; every other language is its own tag. */
function localeOf(language: string): string {
  return language === 'en' ? 'en-US' : language;
}

/**
 * ★ THE CABINET'S LANGUAGE, NOT THE BROWSER'S (design 24, T16; the owner, 2026-10-11: "should
 * follow the cabinet's language"). Every formatter above defaults to it: with no language passed,
 * `Intl` took the browser's, and a teammate's Russian browser printed "9 сент. 2026 г." and
 * "130 804" in an English cabinet.
 *
 * ★ AND IT FOLLOWS THE LANGUAGE CHOSEN (design 29, W5): "Sep 9, 2026" and "130,804" in English,
 * "09.09.2026" and "130.804" in German. A live binding, changed with the catalogue's language, so a
 * default read when a formatter is called, and every screen that reads `CABINET_LOCALE` as it
 * draws, follow at once. A formatter built ONCE at module load keeps the language of that moment:
 * build it per call.
 */
export let CABINET_LOCALE = localeOf(i18n.language);

i18n.on('languageChanged', (language: string) => {
  CABINET_LOCALE = localeOf(language);
});
